import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { AppNavbar, AppFooter, cn } from "@hoalaw/ui";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { appConfig } from "@/config/app";

const TermsOfServiceContent = lazy(() => import("@/components/legal/TermsOfServiceContent"));
const PrivacyPolicyContent = lazy(() => import("@/components/legal/PrivacyPolicyContent"));
const DisclaimerContent = lazy(() => import("@/components/legal/DisclaimerContent"));

const lazyLegalContent = (content: ReactNode) => (
  <Suspense fallback={<p className="text-sm text-gray-500">Loading...</p>}>
    {content}
  </Suspense>
);

/** Keep in-app /tools clicks on the React route so Firebase auth is not lost
 *  to the Astro static page Amplify serves for full document navigations. */
function isSpaToolsLink(href: string | null): boolean {
  if (!href) return false;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin === window.location.origin && (url.pathname === "/tools" || url.pathname === "/tools/");
  } catch {
    return href === "/tools" || href === "/tools/";
  }
}

const Layout = () => {
  const { currentUser, isAdmin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const isChatRoute = pathname === "/chat" || pathname.startsWith("/chat/");

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!anchor || anchor.getAttribute("target") === "_blank") return;
      const href = anchor.getAttribute("href");
      if (!isSpaToolsLink(href)) return;
      event.preventDefault();
      navigate("/tools");
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      toast({ title: "Logged out", description: "You have been successfully logged out" });
      navigate("/");
    } catch {
      toast({ title: "Error", description: "Failed to log out", variant: "destructive" });
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col",
        isChatRoute ? "h-dvh max-h-dvh min-h-0 overflow-hidden" : "min-h-screen"
      )}
    >
      <AppNavbar
        config={appConfig}
        currentUser={currentUser}
        isAdmin={isAdmin}
        loading={loading}
        onLogout={handleLogout}
      />
      <main
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          isChatRoute ? "overflow-hidden" : ""
        )}
      >
        <Outlet />
      </main>
      {!isChatRoute && (
        <AppFooter
          config={appConfig}
          TermsContent={lazyLegalContent(<TermsOfServiceContent />)}
          PrivacyContent={lazyLegalContent(<PrivacyPolicyContent />)}
          DisclaimerContent={lazyLegalContent(<DisclaimerContent />)}
        />
      )}
    </div>
  );
};

export default Layout;
